import { Badge } from "@/components/ui/badge";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Image as ImageIcon, Pencil, Sparkles, Trash2, Zap } from "lucide-react";
import { OFFER_TEMPLATES } from "./OfferTemplateForm";
import { expiryLabel, isExpiringSoon } from "./OfferFilterBar";
import { offerLifecycle } from "@/lib/campaignStatus";

/**
 * One offer as the merchant sees it.
 *
 * Built here in Phase 4 rather than inside the editor because the Discovery Offers page
 * (spec 10.2/10.4, Phase 6) renders the same card - campaign badge, tier badge, Smart
 * indicator, preview. Keeping it separate means building it once.
 *
 * `status` is derived server-side from the owning campaign, never stored on the offer,
 * so the two cannot drift.
 */

const TEMPLATE_LABELS = Object.fromEntries(
  OFFER_TEMPLATES.map((t) => [t.value, t.label])
);

const TIER_STYLES = {
  BRONZE: "bg-amber-700/10 text-amber-700 border-amber-700/30",
  SILVER: "bg-slate-400/10 text-slate-500 border-slate-400/30",
  GOLD: "bg-yellow-500/10 text-yellow-600 border-yellow-500/30",
};

export default function OfferCard({
  offer,
  onEdit,
  onDelete,
  showCampaignBadge = false,
  busy = false,
  actions,        // a slot for the three-dot menu (spec 10.2.6); replaces the icons
  showPreview = false, // the customer-facing Discovery preview (spec 10.2.2)
  // The Content this offer renders (round 7, SP-26). The owner resolves it, because it
  // knows which content the campaign carries and this component stays presentational.
  creative = null,
  // What the campaign renders when the offer has no creative of its own. Shown as the
  // fallback rather than an empty box: pick_offer_creative falls back to exactly this
  // at serving time, so a blank square would misrepresent what a customer sees.
  campaignCreative = null,
}) {
  const isSmart = offer.offer_type === "SMART";
  const isBoost = offer.source === "DISCOVERY_BOOST";
  // Not offer.status. That is the serving-shaped answer, in which a DRAFT campaign's
  // offers are EXPIRED - so a merchant building a draft saw every offer they had just
  // made dimmed and badged Expired (round 7, SP-32). offerLifecycle answers from the
  // raw campaign status, and PAUSED gets the same correction.
  const lifecycle = offerLifecycle(offer);
  const expired = lifecycle.expired;
  // The offer's OWN image only. It used to fall back to the campaign's, matching
  // pick_offer_creative - but round 12's FP-21 changed what an offer with no image of
  // its own actually renders, and it is now the account logo. A thumbnail captioned
  // "using the campaign's creative" would be describing a resolution that no longer
  // happens. campaignCreative is still accepted so callers need not change, and is used
  // only to say what the offer is NOT using.
  const shownCreative = creative;

  // What the customer preview renders (round 9, TP-22). `creative_url` is resolved by
  // GET /offers, through the same order serving uses, so the preview is of the image
  // the customer actually gets. The locally-known creative is a fallback for the
  // in-campaign case, where the card is fed a creative directly and the offer may not
  // have been refetched yet.
  const rawPreview = offer.creative_url || offer.preview_url || shownCreative?.master_url;
  const previewUrl = !rawPreview || rawPreview.startsWith("http") || rawPreview.startsWith("data:")
    ? rawPreview
    : `${API.replace(/\/api\/?$/, "")}${rawPreview}`;

  return (
    <Card className={`h-full flex flex-col ${lifecycle.dim ? "opacity-70" : ""}`}>
      {/* p-3 and tighter spacing (SP-26): "the containers are too larger ... they can be
          much much smaller as seen in the image where I roughly sketched them." */}
      {/* flex-col + h-full so every card in a row ends at the same baseline
          however much free text it carries (round 9, TP-11). */}
      <CardContent className="p-3 space-y-2 flex flex-col flex-1">
        <div className="flex items-start justify-between gap-2">
          {/* The creative for THIS offer, which is the other half of SP-26.
              Round 12, FP-20: "The space inside the offer templates could be used a lot
              better by simply making the images bigger". 44px was a marker that an image
              existed rather than a look at it, on a card several hundred pixels wide.
              64px is still a thumbnail and not a preview - the customer preview below is
              what a preview looks like - but it is now large enough to tell two
              creatives apart, which is the whole reason it is here.

              Rendered even with no creative (FP-7): the box was what pushed the text
              column across, so a card with an image and a card without started their
              text in different places and ended at different heights. */}
          <div
            className="h-16 w-16 shrink-0 rounded border border-border overflow-hidden bg-muted/40 flex items-center justify-center"
            title={shownCreative
              ? `This offer uses ${shownCreative.name}`
              : campaignCreative
                ? "No image chosen for this offer, so your company logo is used"
                : "No image chosen for this offer"}
          >
            {shownCreative && (
              shownCreative.master_url ? (
                <img
                  src={shownCreative.master_url.startsWith("http") || shownCreative.master_url.startsWith("data:")
                    ? shownCreative.master_url
                    : `${API.replace(/\/api\/?$/, "")}${shownCreative.master_url}`}
                  alt={shownCreative.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <ImageIcon className="h-5 w-5 text-muted-foreground/50" />
              )
            )}
            {!shownCreative && <ImageIcon className="h-5 w-5 text-muted-foreground/30" />}
          </div>
          <div className="min-w-0 space-y-0.5 flex-1">
            <p className="font-medium leading-tight truncate text-sm">{offer.display_value}</p>
            <p className="text-[11px] text-muted-foreground">
              {TEMPLATE_LABELS[offer.template] || offer.template}
            </p>
          </div>

          <div className="flex items-center gap-1 shrink-0">
            {actions}
            {!actions && onEdit && (
              <Button
                variant="ghost" size="icon" className="h-8 w-8"
                onClick={() => onEdit(offer)} disabled={busy}
                aria-label="Edit offer"
              >
                <Pencil className="h-3.5 w-3.5" />
              </Button>
            )}
            {!actions && onDelete && (
              <Button
                variant="ghost" size="icon"
                className="h-8 w-8 text-muted-foreground hover:text-destructive"
                onClick={() => onDelete(offer)} disabled={busy}
                aria-label="Remove offer"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        </div>

        {/* Round 12, FP-7: "In edit mode distance from bottom of image on offer is
            inconsistent".
            The card stretches to its row (TP-11) but its contents stack from the top, so
            everything left over fell to the bottom - and how much was left over depended
            on whether that particular offer carried badges, Smart bounds or conditions.
            Every card in the row was the same height and no two had the same gap under
            the image, which is the thing that actually looks wrong.
            The variable rows now share one reserved block, so the slack is the same on
            every card whatever it holds. min-h rather than a fixed h: an offer with four
            badges and two lines of conditions still grows, and takes its whole row with
            it, which is TP-11 working as intended. */}
        <div className="flex-1 space-y-2 min-h-[3.5rem]">
        <div className="flex flex-wrap items-center gap-1.5">
          {/* Smart indicator - Gold only, per spec 10.2 */}
          {isSmart && (
            <Badge variant="outline" className="gap-1 bg-yellow-500/10 text-yellow-600 border-yellow-500/30">
              <Sparkles className="h-3 w-3" />
              Smart Offer
            </Badge>
          )}
          {isSmart && offer.optimisation_strength && (
            <Badge variant="outline" className="text-[10px]">
              {offer.optimisation_strength.charAt(0) + offer.optimisation_strength.slice(1).toLowerCase()}
            </Badge>
          )}

          {/* Boost offers sit outside the tier limit and never run in Journey Mode (8.1) */}
          {isBoost && (
            <Badge variant="outline" className="gap-1 bg-purple-500/10 text-purple-600 border-purple-500/30">
              <Zap className="h-3 w-3" />
              Discovery Boost
            </Badge>
          )}

          {/* Tier badge is CRM-only per spec 10.2 - never shown to customers */}
          {showCampaignBadge && offer.campaign_tier && (
            <Badge variant="outline" className={TIER_STYLES[offer.campaign_tier] || ""}>
              {offer.campaign_tier.charAt(0) + offer.campaign_tier.slice(1).toLowerCase()}
            </Badge>
          )}
          {showCampaignBadge && offer.campaign_name && (
            <Badge variant="secondary" className="max-w-[160px] truncate">
              {offer.campaign_name}
            </Badge>
          )}

          {lifecycle.label && (
            <Badge variant="outline" className="text-muted-foreground">{lifecycle.label}</Badge>
          )}

          {/* The countdown G-17 asked for, on the offers it matters for. Deliberately
              not on every card: "Expiring in 118 days" is noise, and it would bury the
              handful that need attention, which is what the filter exists to surface. */}
          {!expired && isExpiringSoon(offer) && (
            <Badge variant="outline" className="border-amber-500/40 text-amber-600">
              {expiryLabel(offer)}
            </Badge>
          )}
        </div>

        {isSmart && offer.smart_bounds && (
          <p className="text-xs text-muted-foreground line-clamp-2">
            Booma may optimise between{" "}
            <span className="font-medium text-foreground">{offer.smart_bounds.minimum}</span> and{" "}
            <span className="font-medium text-foreground">{offer.smart_bounds.maximum}</span>
            , preferring {offer.smart_bounds.preferred}.
          </p>
        )}

        {/* Clamped. This is merchant-written free text with no length limit, and
            it was the main reason one card in a row stood taller than its neighbours.
            The full text is on the offer itself and in the title. */}
        {offer.payload?.conditions && (
          <p className="text-xs text-muted-foreground italic line-clamp-2" title={offer.payload.conditions}>
            {offer.payload.conditions}
          </p>
        )}
        </div>

        {/* spec 10.2.2, rebuilt in round 9 (TP-22).
            The card shows the offer as a customer sees it in Discovery: the square
            creative with the value across it, not a thumbnail beside a line of text.
            Deliberately renders only what a customer would get - no tier badge and no
            Smart indicator, both of which spec 10.2 marks CRM-only. Showing a merchant
            their own tier inside a "this is what your customer sees" preview would be
            actively misleading.

            The image was blank on every card until this round: nothing ever set
            creative_url, so the fallback grey box was the whole preview. */}
        {showPreview && (
          <div className="mt-auto rounded-lg border border-border overflow-hidden bg-muted/30">
            <div className="px-2.5 py-1.5 border-b border-border bg-muted/50">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
                Customer preview
              </p>
            </div>
            <div className="relative aspect-square bg-muted">
              {previewUrl ? (
                /* object-COVER here, unlike the thumbnail above. This box is square and
                   so is the rendition, so there is nothing to crop, and cover is what
                   the Discovery card itself does. */
                <img src={previewUrl} alt="" className="absolute inset-0 h-full w-full object-cover" />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center">
                  <ImageIcon className="h-8 w-8 text-muted-foreground/40" />
                </div>
              )}
              {/* A gradient, not a solid band: the value has to stay legible over a
                  photograph without hiding the third of the image it sits on. */}
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/50 to-transparent p-2.5 pt-8">
                <p className="text-sm font-semibold text-white leading-tight drop-shadow">
                  {offer.display_value}
                </p>
                {offer.campaign_name && (
                  <p className="text-[11px] text-white/80 truncate">{offer.campaign_name}</p>
                )}
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
